import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { findHits, splitByQuery } from "./chatSearch";
import { dayKey, dayLabel, timeLabel } from "./chatTime";

export type ChatRole = "user" | "assistant";

export interface ChatTask {
  title: string;
  role: string;
  objective: string;
  deliverable: string;
}

export interface ChatMessage {
  id: string;
  request_id: string;
  role: ChatRole;
  text: string;
  created_at: string;
  tasks: ChatTask[];
}

export interface ChatPending {
  request_id: string;
  status: "queued" | "running";
}

export interface ChatThreadProps {
  messages: ChatMessage[];
  pending: ChatPending | null;
  lang: "hr" | "en";
  now: Date;
  seenId: string;
  query: string;
  activeHit: number;
}

const COPY = {
  hr: {
    newDivider: "Novo",
    proposal: "Prijedlog · nije pokrenuto",
    goal: "Cilj",
    result: "Rezultat",
    jump: "Na najnovije",
    queued: "Poruka je zaprimljena i čeka obradu.",
    running: "Koordinator priprema odgovor.",
    resend: " Ne šalji je ponovno.",
    empty:
      "Prvi korak: opiši cilj za MinbarLive leadove i zalijepi poveznice koje želiš uzeti u obzir.",
    tasksNote:
      "Ovo su prijedlozi koraka, ne pokrenuti poslovi. Pretragu leadova pokrećete u Organizacije → Pronađi leadove.",
  },
  en: {
    newDivider: "New",
    proposal: "Proposal · not started",
    goal: "Goal",
    result: "Result",
    jump: "Jump to latest",
    queued: "The message was received and is waiting.",
    running: "The coordinator is preparing an answer.",
    resend: " Do not send it again.",
    empty:
      "First step: describe the goal for the MinbarLive leads and paste the links to consider.",
    tasksNote:
      "These are suggested steps, not started jobs. Start a lead search in Organizations → Find leads.",
  },
} as const;

export function ChatThread({
  messages,
  pending,
  lang,
  now,
  seenId,
  query,
  activeHit,
}: ChatThreadProps) {
  const t = COPY[lang];
  const elRef = useRef<HTMLDivElement | null>(null);
  const [showJump, setShowJump] = useState(false);
  const activeNodeRef = useRef<HTMLElement | null>(null);

  const nearBottom = () => {
    const el = elRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight <= 80;
  };

  const scrollToBottom = () => {
    const el = elRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };

  // Initial mount: jump to the latest message and clear the jump indicator.
  useLayoutEffect(() => {
    scrollToBottom();
    setShowJump(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // New messages / pending change: follow the bottom only if the user is near it.
  useLayoutEffect(() => {
    if (nearBottom()) {
      scrollToBottom();
      setShowJump(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, pending]);

  // Bring the active search hit into view.
  useEffect(() => {
    const node = activeNodeRef.current;
    if (node && typeof node.scrollIntoView === "function") {
      node.scrollIntoView({ block: "center" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeHit, query]);

  const onScroll = () => {
    const el = elRef.current;
    if (!el) return;
    setShowJump(el.scrollHeight - el.scrollTop - el.clientHeight > 80);
  };

  const jumpToLatest = () => {
    scrollToBottom();
    setShowJump(false);
  };

  const seenIndex = seenId ? messages.findIndex((m) => m.id === seenId) : -1;
  const showNewDivider =
    seenIndex >= 0 && seenIndex < messages.length - 1;

  // Global hit counter across messages, matching the order of `findHits`.
  let hitCounter = 0;

  let lastDay: string | null = null;

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div
        ref={elRef}
        data-testid="chat-thread"
        role="log"
        aria-live="polite"
        className="flex-1 overflow-y-auto px-3 py-3"
        onScroll={onScroll}
      >
        <div className="space-y-3">
          {messages.length === 0 ? (
            <p
              data-testid="chat-empty"
              className="rounded-2xl border border-dashed border-border p-4 text-center text-sm text-muted-foreground"
            >
              {t.empty}
            </p>
          ) : null}

          {messages.map((m, index) => {
            const dk = dayKey(m.created_at);
            const showDay = dk !== lastDay;
            lastDay = dk;
            const segments = splitSegments(m.text, query);
            const hitBase = hitCounter;
            hitCounter += hitsInMessage(m, query);

            return (
              <div key={m.id}>
                {showDay ? (
                  <div
                    data-testid={`chat-day-${dk}`}
                    className="my-2 text-center text-[11px] text-muted-foreground"
                  >
                    {dayLabel(m.created_at, now, lang)}
                  </div>
                ) : null}

                {showNewDivider && index === seenIndex + 1 ? (
                  <div
                    data-testid="chat-new-divider"
                    className="my-2 flex items-center gap-2 text-[11px] text-muted-foreground"
                  >
                    <span className="h-px flex-1 bg-border" />
                    <span>{t.newDivider}</span>
                    <span className="h-px flex-1 bg-border" />
                  </div>
                ) : null}

                <article
                  data-testid={`chat-msg-${m.id}`}
                  data-role={m.role}
                  className={`max-w-[85%] whitespace-pre-wrap break-words rounded-2xl px-3.5 py-2.5 text-sm ${
                    m.role === "user"
                      ? "ml-auto bg-primary text-primary-foreground"
                      : "mr-auto bg-muted"
                  }`}
                >
                  <div>
                    {renderSegments(
                      segments,
                      hitBase,
                      activeHit,
                      activeNodeRef,
                    )}
                  </div>

                  <time
                    dateTime={m.created_at}
                    className="mt-1 block text-[11px] opacity-70"
                  >
                    {timeLabel(m.created_at, lang)}
                  </time>

                  {m.role === "assistant" && m.tasks && m.tasks.length > 0 ? (
                    <div className="space-y-2">
                      {m.tasks.map((task, ti) => (
                        <details
                          key={`${m.id}-task-${ti}`}
                          className="mt-2 rounded-lg border border-border bg-background/60 p-2 text-xs"
                        >
                          <summary className="cursor-pointer font-medium">
                            {task.title}
                          </summary>
                          <div className="mt-1 space-y-1 text-muted-foreground">
                            <p>{t.proposal}</p>
                            <p>{task.role}</p>
                            <p>
                              <span className="font-medium">{t.goal}: </span>
                              {task.objective}
                            </p>
                            <p>
                              <span className="font-medium">{t.result}: </span>
                              {task.deliverable}
                            </p>
                          </div>
                        </details>
                      ))}
                    </div>
                  ) : null}

                  {m.role === "assistant" && m.tasks && m.tasks.length > 0 ? (
                    <p
                      data-testid="chat-tasks-note"
                      className="mt-2 text-xs text-muted-foreground"
                    >
                      {t.tasksNote}
                    </p>
                  ) : null}
                </article>
              </div>
            );
          })}

          {pending ? (
            <p
              data-testid="chat-pending"
              role="status"
              className="rounded-lg bg-muted p-3 text-sm"
            >
              {(pending.status === "queued" ? t.queued : t.running) + t.resend}
            </p>
          ) : null}
        </div>
      </div>

      {showJump ? (
        <button
          type="button"
          data-testid="chat-jump-latest"
          aria-label={t.jump}
          onClick={jumpToLatest}
          className="absolute bottom-3 right-3 flex min-h-11 min-w-11 items-center justify-center rounded-full bg-primary text-primary-foreground shadow"
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 5v14" />
            <path d="M6 13l6 6 6-6" />
          </svg>
        </button>
      ) : null}
    </div>
  );
}

type SegmentLike = string | { text?: string; hit?: boolean };

export interface ChatHit {
  messageId: string;
  index: number;
}

/**
 * Number of hits in a single message, as reported by `findHits`. Hits are
 * ordered per message, so summing the per-message counts yields the same
 * global order as `findHits(messages, query)`.
 */
function hitsInMessage(message: ChatMessage, query: string): number {
  if (!query) return 0;
  try {
    const hits = findHits([message], query) as unknown as unknown[];
    return Array.isArray(hits) ? hits.length : 0;
  } catch {
    return 0;
  }
}

/**
 * Splits a message text into text/hit segments using `splitByQuery`. Hit
 * segments are objects or strings paired with the hit flag; we normalise to a
 * uniform shape so the renderer only has to deal with `{ text, hit }`.
 */
function splitSegments(text: string, query: string): SegmentLike[] {
  if (!query) return [text];
  try {
    return splitByQuery(text, query) as unknown as SegmentLike[];
  } catch {
    return [text];
  }
}

function isHitSegment(seg: SegmentLike | null | undefined): boolean {
  return (
    seg !== null &&
    seg !== undefined &&
    typeof seg === "object" &&
    Boolean((seg as { hit?: boolean }).hit)
  );
}

function segText(seg: SegmentLike | null | undefined): string {
  if (typeof seg === "string") return seg;
  if (seg && typeof seg === "object" && typeof seg.text === "string") {
    return seg.text;
  }
  return "";
}

function renderSegments(
  segments: SegmentLike[],
  hitBase: number,
  activeHit: number,
  activeNodeRef: { current: HTMLElement | null },
): ReactNode {
  let localHit = 0;
  return segments.map((seg, i) => {
    if (isHitSegment(seg)) {
      const globalIndex = hitBase + localHit;
      localHit += 1;
      const isActive = globalIndex === activeHit;
      return (
        <mark
          key={`h-${i}`}
          data-testid="chat-hit"
          data-active={isActive ? "true" : undefined}
          ref={
            isActive
              ? (node: HTMLElement | null) => {
                  activeNodeRef.current = node;
                }
              : undefined
          }
          className={
            isActive
              ? "rounded bg-yellow-300 px-0.5 font-semibold text-black"
              : "rounded bg-yellow-200/70 px-0.5 text-inherit"
          }
        >
          {segText(seg)}
        </mark>
      );
    }
    return segText(seg);
  });
}
